import { useQuery } from "@apollo/client";
import { Box, Card, CardContent, CardHeader, styled } from "@mui/material";
import { sortBy } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import {
  GetThreadCampaignContactsQuery,
  GetThreadCampaignContactsQueryVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import TagChip from "~frontend/components/TagChip";
import { ScriptTags } from "~frontend/components/responseScripts/styles";
import { GET_THREAD_CAMPAIGN_CONTACTS } from "~frontend/graphql/campaignContacts";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import formatTagName from "~frontend/util/formatTagName";

import lastMessagedTime from "./lastMessagedTime";
import { Thread } from "./types";

const Label = styled("span")({
  fontWeight: 600,
});

export default function ContactHistory({
  thread,
  scrollToCampaign,
}: {
  thread: Thread;
  scrollToCampaign: (campaignId: string) => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const campaignContactIds = useMemo(
    () =>
      Array.from(
        new Set(
          thread.messages
            .filter(m => m.campaignContactId)
            .map(m => m.campaignContactId!)
        )
      ),
    [thread.messages]
  );

  const { data, error, loading } = useQuery<
    GetThreadCampaignContactsQuery,
    GetThreadCampaignContactsQueryVariables
  >(GET_THREAD_CAMPAIGN_CONTACTS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
        campaignContactIds,
      },
    },
  });

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  const sortedData = sortBy(
    data!.getCampaignContacts.map(cc => ({
      ...cc,
      lastMessagedTime: lastMessagedTime(cc.id, thread.messages),
    })),
    cc => (cc.lastMessagedTime ? -cc.lastMessagedTime : Number.MAX_SAFE_INTEGER)
  );

  return (
    <Card>
      <CardHeader
        sx={{
          padding: "6px 16px",
        }}
        title={
          <Box
            sx={{
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            <FormattedMessage
              defaultMessage="Campaigns in This Thread"
              description="Header for list of campaigns in a thread"
            />
          </Box>
        }
      />

      <CardContent>
        {sortedData.map(cc => (
          <Box key={cc.id} sx={{ marginBottom: "16px" }}>
            <Box
              sx={{
                marginBottom: "6px",
              }}
            >
              <LinkButton
                onClick={evt => {
                  evt.preventDefault();
                  scrollToCampaign(cc.campaign.id);
                }}
                sx={{
                  color: palette.navy,
                  fontWeight: 600,
                }}
              >
                {cc.campaign.name}
              </LinkButton>
            </Box>
            <Box sx={{ paddingLeft: "16px", fontSize: "12px" }}>
              <Box>
                <Label>
                  <FormattedMessage
                    defaultMessage="Last Messaged"
                    description="Label for when the last message in a thread from a campaign was"
                  />
                  :
                </Label>{" "}
                {cc.lastMessagedTime &&
                  DateTime.fromMillis(cc.lastMessagedTime).toLocaleString(
                    DateTime.DATETIME_SHORT
                  )}
              </Box>
              <Box sx={{ marginBottom: "8px" }}>
                <Label>
                  <FormattedMessage
                    defaultMessage="Started"
                    description="Label for when a campaign was started"
                  />
                  :
                </Label>{" "}
                {DateTime.fromMillis(cc.campaign.startedAt).toLocaleString(
                  DateTime.DATETIME_SHORT
                )}
              </Box>
              {cc.tags.length ? (
                <ScriptTags>
                  {cc.tags.map(tag => (
                    <TagChip key={tag.id} label={formatTagName(tag.tag)} />
                  ))}
                </ScriptTags>
              ) : null}
              {cc.note && (
                <Box sx={{ marginBottom: "8px" }}>
                  <Label>
                    <FormattedMessage
                      defaultMessage="Contact notes"
                      description="Label for contact notes from a past campaign"
                    />
                    :
                  </Label>{" "}
                  {cc.note.text}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}
